import log from '@/lib/logger';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {useMembersTranslation, useCommonTranslation} from '@/i18n/hooks';
import {Dialog, DialogActions, DialogBody, DialogTitle} from '@/components/tailwind/dialog';
import {Button} from '@/components/tailwind/button';
import {Input} from '@/components/tailwind/input';
import ValidationAlert from "@/components/validation-alert.tsx";
import {Field, Label} from "@/components/tailwind/fieldset.tsx";
import {createAnnualDonation, updateAnnualDonation} from "@/services/donation-services.ts";
import {useAuth} from "@/context/use-auth.tsx";
import {useEffect, useMemo} from "react";
import {AnnualDonation} from "@/models/donation.ts";

interface AnnualDonationDialogProps {
    isOpen: boolean;
    mode: 'add' | 'edit';
    memberId: string;
    donation?: AnnualDonation;
    onSave: (donation: AnnualDonation) => void;
    onClose: () => void;
}

const annualDonationSchema = z.object({
    amount: z.string()
        .min(1, 'donationDialogs.annual.errors.amount-required')
        .refine((value) => {
            const num = parseFloat(value.replace(',', '.'));
            return !isNaN(num) && num > 0;
        }, 'donationDialogs.annual.errors.amount-positive'),

    // validFrom is set automatically to today - no user validation needed
    validFrom: z.string()
});

export type AnnualDonationFormData = z.infer<typeof annualDonationSchema>;

export default function AnnualDonationDialog({
    isOpen,
    mode,
    memberId,
    donation,
    onSave,
    onClose,
}: AnnualDonationDialogProps) {
    const {t} = useMembersTranslation();
    const {t: tCommon} = useCommonTranslation();
    const {token} = useAuth();

    const defaultValues = useMemo(() => ({
        amount: mode === 'edit' && donation ? donation.amount.toFixed(2).replace('.', ',') : '',
        // validFrom is always set to today - both for add and edit
        validFrom: new Date().toISOString().split('T')[0]
    }), [mode, donation]);

    const {
        register,
        handleSubmit,
        setError,
        formState: {errors},
        reset
    } = useForm<AnnualDonationFormData>({
        resolver: zodResolver(annualDonationSchema),
        defaultValues
    });

    useEffect(() => {
        if (isOpen) {
            reset(defaultValues);
        }
    }, [isOpen, reset, defaultValues]);

    async function saveData(data: AnnualDonationFormData): Promise<boolean> {
        const amountValue = parseFloat(data.amount.replace(',', '.'));

        try {
            let response: AnnualDonation | void;
            if (mode === 'add') {
                response = await createAnnualDonation(token, memberId, {
                    amount: amountValue,
                    validFrom: data.validFrom
                });
            } else {
                response = await updateAnnualDonation(token, memberId, donation!.id, {
                    amount: amountValue,
                    validFrom: data.validFrom
                });
            }
            if (response) {
                onSave(response);
            }
            return true;
        } catch (error: unknown) {
            log.error("Error saving annual donation: ", error);
            const err = error as { status?: number; error?: string; message?: string };
            if (err.status) {
                setError("root", {
                    message: tCommon("serverError.response-code-with-details", {
                        statusCode: err.status,
                        errorDetails: err.error
                    })
                });
            } else {
                setError("root", {message: tCommon("serverError.unknown-with-details", {errorDetails: err.message})});
            }
            return false;
        }
    }

    async function onSubmit(data: AnnualDonationFormData) {
        if (await saveData(data)) {
            handleClosing();
        }
    }

    function handleClosing() {
        reset();
        onClose();
    }

    const dialogTitle = mode === 'add'
        ? t('donationDialogs.annual.titleAdd')
        : t('donationDialogs.annual.titleEdit');

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogTitle>
                {dialogTitle}
            </DialogTitle>

            <DialogBody>
                {errors.root && (
                    <ValidationAlert message={errors.root?.message ||
                        t('donationDialogs.annual.errors.unknown-server-issue')}/>
                )}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <Field>
                            <Label>
                                {t('donationDialogs.annual.fields.amount.label')}
                            </Label>
                            <Input
                                type="text"
                                inputMode="decimal"
                                id="amount"
                                {...register('amount')}
                                invalid={Boolean(errors.amount)}
                                aria-label={t('donationDialogs.annual.fields.amount.ariaLabel')}
                                data-testid="annual-donation-amount"
                            />
                            {errors.amount && (
                                <ValidationAlert
                                    message={t(errors.amount?.message ||
                                        "donationDialogs.annual.errors.unknown-amount-issue")}
                                />
                            )}
                        </Field>
                    </div>

                    <DialogActions>
                        <Button
                            type="button"
                            plain
                            onClick={handleClosing}
                            aria-label={tCommon('buttons.cancel.ariaLabel')}
                        >
                            {tCommon('buttons.cancel.text')}
                        </Button>
                        <Button
                            type="submit"
                            aria-label={tCommon('buttons.submit.ariaLabel')}
                            data-testid="submit-annual-donation"
                        >
                            {tCommon('buttons.submit.text')}
                        </Button>
                    </DialogActions>
                </form>
            </DialogBody>
        </Dialog>
    );
}
